import moment from 'moment';
import ModelProxy, {required, optional} from './proxy';
import {
  ALL_GENES,
  HIV_SPECIES,
} from '../constants';


const REGEX_GENE_MUTATION = {
  PR: /^[AC-IK-WY]?[1-9]\d?([AC-IK-WY]+(_[AC-IK-WY]+)?|-)$/,
  RT: /^[AC-IK-WY]?([1-9]\d?|[1-4]\d\d|5[0-5]\d|560)([AC-IK-WY]+(_[AC-IK-WY]+)?|-)$/,
  IN: /^[AC-IK-WY]?([1-9]\d?|1\d\d|2[0-7]\d|28[0-8])([AC-IK-WY]+(_[AC-IK-WY]+)?|-)$/,
  CA: /^[AC-IK-WY]?([1-9]\d?|1\d\d|2[0-2]\d|23[0-1])([AC-IK-WY]+(_[AC-IK-WY]+)?|-)$/,
};


export function createExampleRawSequenceData(props) {
  const incomplete = props.seqType === 'IncompleteSequence' || props.seqType === 'PartialMutationList';
  return new RawSequenceData({
    existingPatientId: incomplete ? 11630 : '',
    ptAlias: incomplete ? 'LabStrain' : 'PATIENT',
    isChild: 'No',
    isolateName: 'ISOLATE',
    isolateDateMatch: '=',
    isolateDate: (new Date()).toISOString().slice(0, 10),
    isolateType: incomplete ? 'Lab' : 'Clinical',
    species: 'HIV1',
    host: 'HUMAN',
    region: incomplete ? 'France' : 'U.S.',
    seqType: 'Sequence',
    seqTemplate: incomplete ? '' : 'PCR',
    seqMethod: incomplete ? '' : 'Dideoxy',
    source: incomplete ? '' : 'Plasma',
    culture: incomplete ? '' : 'No',
    cloneMethod: incomplete ? '' : 'None',
    parent: incomplete ? 'HXB2' : '',
    labSdm: incomplete ? 'Yes' : '',
    sequence: '',
    ...props
  });
}


export default class RawSequenceData extends ModelProxy {

  static propTypes = {
    uuid: required,
    existingRefId: optional,
    existingPatientId: optional,
    refName: required,
    extraRefNames: optional,
    accession: required,
    ptAlias: required,
    isChild: required,
    isolateName: required,
    isolateDateMatch: required,
    isolateDate: required,
    isolateType: required,
    species: required,
    host: required,
    region: required,
    seqTemplate: optional,
    seqMethod: optional,
    seqType: required,
    authorSubtype: optional,
    cloneMethod: optional,
    cloneName: optional,
    source: optional,
    culture: optional,
    parent: optional,
    labSdm: optional,
    labPassage: optional,
    suscReplicaCap: optional,
    suscRangeLow: optional,
    suscRangeHigh: optional,
    existingGenes: optional,
    definition: optional,
    sequence: required,
    mutationMap: optional,
    hivdbDRMs: optional,
  };

  static defaultProps = {
    isolateType: 'Clinical',
    host: 'Human',
    seqTemplate: 'PCR',
    seqMethod: 'Dideoxy',
    seqType: 'Sequence',
    cloneMethod: 'None',
    cloneName: '$$AUTO$$',
    // source: 'Plasma',
    culture: 'No',
    isChild: 'No',
  };

  static setExistingPatientId(self, value) {
    if (typeof(value) === 'string' && !/^\d+$/.test(value)) {
      // process graphql's ID
      value = atob(value).replace(/^Patient:/, '');
    }
    if (value) {
      self.props.existingPatientId = parseInt(value);
    }
  }


  static getSpecies(self) {
    let {species} = self.props;
    if (!species) {
      return null;
    }
    if (/HIV-?2|HUMAN IMMUNODEFICIENCY VIRUS (?:TYPE )?2/i.test(species)) {
      species = 'HIV2';
    }
    else if (/HIV|HUMAN IMMUNODEFICIENCY VIRUS/i.test(species)) {
      species = 'HIV1';
    }
    else if (/SIV|SIMIAN IMMUNODEFICIENCY VIRUS/i.test(species)) {
      species = 'NHPL';
    }
    return species;
  }

  static getAuthorSubtype(self) {
    let {authorSubtype} = self.props;
    if (this.getSpecies(self) === 'HIV2') {
      if (authorSubtype === 'A' || authorSubtype === 'B') {
        authorSubtype = `Group ${authorSubtype}`;
      }
    }
    if ((typeof authorSubtype) === 'string') {
      if (authorSubtype.startsWith('recombinant of')) {
        authorSubtype = authorSubtype.replace('recombinant of', '').trim();
      }
    }

    return authorSubtype;
  }

  static validateSpecies(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    } else if (!HIV_SPECIES.includes(value)) {
      msgs.push({
        text: `Species must be "HIV1", "HIV2" or "NHPL", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateSequence(self, value) {
    let msgs = [];
    if (!value) {
      return msgs;
    }
    else if (value.startsWith('$$MUTATIONS$$')) {
      msgs = msgs.concat(this.validateMutationList(self, this.getMutationList(self)));
    }
    else if (/NNN/.test(value)) {
      msgs.push({
        text: 'Sequence contains at least 3 continuous "N"s.',
        level: 'warning'
      });
    }
    return msgs;
  }

  static validateGene(self, value) {
    const msgs = [];
    if (ALL_GENES.indexOf(value) === -1) {
      msgs.push({
        text: `Property "gene" has invalid gene name "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateMutationList(self, value) {
    const msgs = [];
    const invalidMuts = [];
    const gene = this.getGene(self);
    for (const mut of value) {
      if (!mut || !(REGEX_GENE_MUTATION[gene].test(mut))) {
        invalidMuts.push(mut);
      }
    }
    if (invalidMuts.length > 0) {
      msgs.push({
        text: (
          `MutationList (${gene}) contains invalid mutation(s) with ` +
          `illegal format: ${invalidMuts.join(', ')}.`),
        level: 'error'
      });
    }
    return msgs;
  }

  static validateSeqType(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (!/^Sequence|Consensus|IncompleteSequence|PartialMutationList$/.test(value)) {
      msgs.push({
        text: `SeqType must be "Sequence", "Consensus", "IncompleteSequence" or "PartialMutationList", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static getIsolateDate(self) {
    let {isolateDate} = self.props;
    if (!isolateDate) {
      return null;
    }
    const isolateDate_str = isolateDate.replace(/^(<=?|>=?|=)/, '');

    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(isolateDate_str)) {
      return isolateDate;
    }

    if (/^\d{4}$/.test(isolateDate_str)) {
      isolateDate = `${isolateDate_str}-06-01`;
      return isolateDate;
    }

    const hyphen_count = (isolateDate_str.match(/-/g) || []).length;
    if (hyphen_count === 2) {
      isolateDate = moment.utc(isolateDate_str, 'DD-MMM-YYYY');
    } else if (hyphen_count === 1) {
      isolateDate = moment.utc(isolateDate_str, 'MMM-YYYY');
    } else {
      isolateDate = moment.utc(isolateDate_str);
    }

    if (!isolateDate.isValid()) {
      isolateDate = '';
    } else {
      isolateDate = isolateDate.format('YYYY-MM-DD');
    }

    isolateDate = isolateDate.replace(/^-/, '');
    return isolateDate;
  }

  static getIsolateDateMatch(self) {
    let {
      isolateDate,
      isolateDateMatch: dateMatch
    } = self.props;
    if (!isolateDate) {
      return dateMatch;
    }
    dateMatch = dateMatch || '';
    if (!dateMatch && /^(<=?|>=?|=)/.test(isolateDate)) {
      dateMatch = /^(<=?|>=?|=)/.exec(isolateDate)[1];
    }
    dateMatch = dateMatch || '=';
    return dateMatch;
  }

  static getExistingPatientId(self) {
    let {existingPatientId, isolateType} = self.props;
    if (isolateType === 'Lab') {
      // force to use PtID 11630 for lab strains
      return '11630';
    }
    return existingPatientId;
  }

  static getRegion(self) {
    let {region, isolateType} = self.props;
    if (isolateType === 'Lab') {
      // force to use France for lab strains
      return 'France';
    }
    if (!region) {
      return null;
    }
    // remove province/state
    region = region.split(':', 2)[0];
    if (/(united states|^u\.?s\.?a?\.?$)/i.test(region)) {
      region = 'U.S.';
    }
    else if (/(united kingdom|^u\.?k\.?$)/i.test(region)) {
      region = 'U.K.';
    }
    else if (/south africa/i.test(region)) {
      region = 'South Africa';
    }
    return region;
  }

  static getCloneName(self) {
    let {cloneName,
      // ptAlias, isolateName
    } = self.props;
    if ((cloneName || '').toUpperCase() !== '$$AUTO$$') {
      return cloneName || null;
    }
    // if (ptAlias && isolateName) {
    //   if (ptAlias != isolateName) {
    //     const pos = isolateName.indexOf(ptAlias);
    //     if (pos > -1) {
    //       let cloneName = isolateName.slice(pos + ptAlias.length);
    //       cloneName = cloneName.replace(/^[ .~_-]+/, '');
    //       return cloneName;
    //     }
    //   }
    // }
    return null;
  }

  static getAccession(self) {
    let {accession, isolateName, seqType} = self.props;
    if (typeof seqType === 'undefined') {
      seqType = self.constructor.defaultProps.seqType;
    }
    return (seqType === 'Sequence' || seqType === 'Consensus') ? accession : isolateName;
  }

  static getPtAlias(self) {
    let {ptAlias} = self.props;
    if (ptAlias) {
      return ptAlias;
    }
    // const pattern = /patient (\S+)/i;
    // if (host && pattern.test(host)) {
    //   ptAlias = pattern.exec(host)[1];
    // }
    // else if (isolateName) {
    //   if (/patient \S+/i.test(isolateName)) {
    //     ptAlias = pattern.exec(isolateName)[1];
    //   }
    //   else {
    //     ptAlias = isolateName;
    //   }
    // }
    // return ptAlias;
  }

  static getHost(self) {
    let {host} = self.props;
    if (!host) {
      return 'HUMAN';
    }
    const pattern = /\b(woman|man|human|homo sapiens)\b/i;
    if (pattern.test(host)) {
      host = 'HUMAN';
    }
    return host;
  }

  static getGene(self) {
    let {sequence, seqType} = self.props;
    const flag = '$$MUTATIONS$$';
    if (sequence && (seqType === 'IncompleteSequence' || seqType === 'PartialMutationList') && sequence.startsWith(flag)) {
      return sequence.slice(flag.length).split('$$', 2)[0];
    }
    return '';
  }

  static setGene(self, value) {
    let {sequence} = self.props;
    let mutations = '';
    const flag = '$$MUTATIONS$$';
    if (sequence && sequence.startsWith(flag)) {
      mutations = sequence.slice(flag.length).split('$$', 2)[1];
    }
    self.props.sequence = `${flag}${value}$$${mutations}`;
  }

  static getMutations(self) {
    let {sequence, seqType} = self.props;
    const flag = '$$MUTATIONS$$';
    if (sequence && (seqType === 'IncompleteSequence' || seqType === 'PartialMutationList') && sequence.startsWith(flag)) {
      return sequence.slice(flag.length).split('$$', 2)[1];
    }
    return '';
  }

  static getMutationsWithGene(self) {
    const mutationList = this.getMutationList(self);
    const gene = this.getGene(self);
    return mutationList.map((mutation) => {
      return gene + mutation;
    }).join(',');
  }

  static setMutations(self, value) {
    let {sequence} = self.props;
    let gene = '';
    const flag = '$$MUTATIONS$$';
    if (sequence && sequence.startsWith(flag)) {
      gene = sequence.slice(flag.length).split('$$', 2)[0];
    }
    self.props.sequence = `${flag}${gene}$$${value}`;
  }

  static getMutationList(self) {
    const mutations = this.getMutations(self);
    if (mutations) {
      return mutations.split(/\s*[,+]\s*/);
    }
    return [];
  }

  static setMutationList(self, value) {
    this.setMutations(self, value.join(', '));
  }

  static setSuscReplicaCap(self, value) {
    self.props.suscReplicaCap = parseFloat(value);
    if (isNaN(self.props.suscReplicaCap)) {
      self.props.suscReplicaCap = null;
    }
  }

  static setSuscRangeLow(self, value) {
    self.props.suscRangeLow = parseFloat(value);
    if (isNaN(self.props.suscRangeLow)) {
      self.props.suscRangeLow = null;
    }
  }

  static setSuscRangeHigh(self, value) {
    self.props.suscRangeHigh = parseFloat(value);
    if (isNaN(self.props.suscRangeHigh)) {
      self.props.suscRangeHigh = null;
    }
  }

  static getMutationGenes(self) {
    const gene = this.getGene(self);
    if (gene) {
      return [gene];
    } else {
      return [];
    }
  }

  static validatePtAlias(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    if (!isNaN(value)) {
      msgs.push({
        text: `Property "ptAlias" is a number (${value}). Be caution when processing the spreadsheet.`,
        level: 'warning'
      });
    }
    if (value.length > 20) {
      msgs.push({
        text: `Property "ptAlias" is longer than 20 characters (${value})`,
        level: 'error',
      });
    }

    // unicode test
    return msgs;
  }

  static validateIsolateName(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    if (!isNaN(value)) {
      msgs.push({
        text: `Property "isolateName" is a number (${value}). Be caution when processing the spreadsheet.`,
        level: 'warning'
      });
    }
    if (value.length > 30) {
      msgs.push({
        text: `Property "isolateName" is longer than 30 characters (${value})`,
        level: 'error',
      });
    }
    return msgs;
  }

  static validateHost(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    if (!/^(HUMAN|CPZ|GOR)$/.test(value)) {
      msgs.push({
        text: `Please double check the virus host "${value}".`,
        level: 'warning'
      });
    }
    return msgs;
  }

  static validateIsolateType(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (!/^(Clinical|Lab)$/.test(value)) {
      msgs.push({
        text: `Property "isolateType" must be "Clinical" or "Lab", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateIsolateDate(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (!moment(value).isValid()) {
      msgs.push({
        text: (
          `Isolate date "${value}" is invalid or is too ambiguous to be recognized. ` +
          'Please use valid date and the recommended format: "YYYY-MM-DD".'
        ),
        level: 'error'
      });
    }
    return msgs;
  }

  static validateIsChild(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (!/^(Yes|No)$/.test(value)) {
      msgs.push({
        text: `Property "isChild" must be "Yes" or "No", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateExistingGenes(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    const genes = value.split(',');
    for (const gene of genes) {
      if (ALL_GENES.indexOf(gene) === -1) {
        msgs.push({
          text: `Property "existingGenes" has invalid gene name "${gene}".`,
          level: 'error'
        });
      }
    }
    if (genes.length) {
      msgs.push({
        text: (
          'The sequence is partially/fully entered before. Retaining' +
          ' this record in the list will trigger an update operation.'
        ),
        level: 'info'
      });
    }
    return msgs;
  }

  static _validateTypedIsolateProp(self, type, key, value, level = 'error') {
    const {isolateType} = self;
    const msgs = [];
    if (isolateType !== type) {
      return [msgs, true];
    }
    if (!value) {
      if (level === 'error') {
        msgs.push({
          text: `Property "${key}" can not be empty when "isolateType" is "${type}".`,
          level: 'error'
        });
      }
      else {
        msgs.push({
          text: (
            `Property "${key}" is empty, however it usually shouldn't be ` +
            `empty for PhenoSense® assay. Please double check.`),
          level: 'warning'
        });
      }
    }
    return [msgs, false];
  }

  static validateSeqTemplate(self, value) {
    const [msgs, return_immediately] = this._validateTypedIsolateProp(self, 'Clinical', 'seqTemplate', value);
    if (return_immediately) {
      return msgs;
    }
    if (value && !/^(PCR|CDNA|Dideoxy)$/.test(value)) {
      msgs.push({
        text: `Please double check property "seqTemplate": "${value}".`,
        level: 'warning'
      });
    }
    return msgs;
  }

  static validateCloneMethod(self, value) {
    const [msgs, return_immediately] = this._validateTypedIsolateProp(self, 'Clinical', 'cloneMethod', value);
    if (return_immediately) {
      return msgs;
    }
    const {seqType} = self.props;
    const cloneName = this.getCloneName(self);
    if (!value) {
      return msgs;
    }
    else if (!/^(BC|Cons|Genomic|MM?C|NA|None|Unknown)$/.test(value)) {
      msgs.push({
        text: `Clone method must be one of "BC", "Cons", "Genomic", "MC", "MMC", "NA", "None" or "Unknown", not "${value}".`,
        level: 'error'
      });
    }
    else if (cloneName && value === 'None') {
      msgs.push({
        text: `Clone method can not be "None" if cloneName (${cloneName}) is specified (the sequence is a clone).`,
        level: 'error'
      });
    }
    else if (seqType !== 'Consensus' && !cloneName && value !== 'None') {
      msgs.push({
        text: `Clone method can not be "None" if cloneName is specified for non-consensus sequence.`,
        level: 'warning'
      });
    }
    else if (!!cloneName && cloneName.length > 10) {
      msgs.push({
        text: `The maximum length of clone name is 10, ${cloneName}`,
        level: 'error',
      });
    }
    return msgs;
  }

  static validateCulture(self, value) {
    const [msgs, return_immediately] = this._validateTypedIsolateProp(self, 'Clinical', 'culture', value);
    if (return_immediately) {
      return msgs;
    }
    if (value && !/^(Yes|No|Unknown)$/.test(value)) {
      msgs.push({
        text: `Property "culture" must be "Yes", "No" or "Unknown", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateSeqMethod(self, value) {
    const [msgs, return_immediately] = this._validateTypedIsolateProp(self, 'Clinical', 'seqMethod', value);
    if (return_immediately) {
      return msgs;
    }
    if (value && !/^(Dideoxy|DNAChip|NGS|Unknown)$/.test(value)) {
      msgs.push({
        text: `Property "seqMethod" must be "Dideoxy", "DNAChip", "NGS" or "Unknown", not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateParent(self, value) {
    const msgs = [];
    const {seqType} = self.props;
    const incomplete = seqType === 'IncompleteSequence' || seqType === 'PartialMutationList';
    if (!incomplete) {
      return msgs;
    }
    if (!value) {
      msgs.push({
        text: 'Property "parent" is required for sequence consistent by mutations.',
        level: 'error'
      });
    }
    if (value && !/^(CONB|HXB2|IIIB|LAI|NL43|RF|p97|pIndieC|MJ4|ROD|EHO)$/.test(value)) {
      msgs.push({
        text: `Please double check property "parent": "${value}".`,
        level: 'warning'
      });
    }
    return msgs;
  }

  static validateLabSdm(self, value) {
    const msgs = [];
    if (value && !/^(Yes|No|)$/.test(value)) {
      msgs.push({
        text: `Property "labSdm" should be "Yes", "No" or empty, not "${value}".`,
        level: 'error'
      });
    }
    return msgs;
  }

  static validateSuscReplicaCap(self, value) {
    const [msgs] = this._validateTypedIsolateProp(self, 'Lab', 'suscReplicaCap', value, 'warning');
    return msgs;
  }

}



// WEBPACK FOOTER //
// ./src/models/raw-sequence-data.js